import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';
import type { CapiProductPage, Raffle } from 'types/capi/pdp';

import HiddenIcon from '~/assets/icons/Hidden';
import { Button } from '~/components/Button';
import Countdown from '~/components/Countdown';
import Modals from '~/components/Modals';
import Tracked from '~/components/Tracked';
import RaffleEntryInteractionEvent from '~/events/RaffleEntryInteractionEvent';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import { useRegistrationStart } from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import { formatRaffleDate } from '~/utils/dates';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

const MONTH_IN_SEC = 60 * 60 * 24 * 31;

type Props = {
  buttonPosition: number;
  raffleListing: Raffle;
};

export default function RaffleListing({
  raffleListing,
  buttonPosition
}: Props) {
  const user = useUser();
  const env = useEnv();
  const region = useRegion();
  const marketingCookies = useMarketingCookies();
  const product = usePageData<CapiProductPage>();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const recordRegistrationStart = useRegistrationStart();
  const { pathname, search } = useLocation();
  const [hasRaffleEnded, setHasRaffleEnded] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { t } = useTranslation('pdp');

  const isGated = region.code !== 'GB' && !user.isLoggedIn;

  const { url, clickRef } = getTrackedUrlAndClickRef(raffleListing, {
    buttonPosition,
    user
  });

  const onButtonClick = () => {
    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    new RaffleEntryInteractionEvent({
      clickref: clickRef,
      guestId: user.guestId,
      isBot,
      price: product.price,
      productId: product.product_id,
      stockStatus: product.stock_status
    }).send();

    return wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product: product,
      session,
      url
    });
  };

  return (
    <div className="rounded-lg bg-white">
      <div className="flex flex-row justify-between gap-3 p-4">
        <div className="flex grow items-center gap-2">
          {isGated ? (
            <div className="rounded-lg bg-grey-tile p-2">
              <HiddenIcon />
            </div>
          ) : (
            <div className="h-9 w-16">
              <img
                src={raffleListing.affiliate.logo}
                alt={raffleListing.id}
                className="rounded bg-white object-cover text-black"
              />
            </div>
          )}
          <div className="text-nowrap text-base">
            {hasRaffleEnded ? (
              <p>{t('closed')}</p>
            ) : (
              <p className="text-forest">{t('openUntil')}</p>
            )}
            <span className="text-sm text-grey-hide">
              {formatRaffleDate(raffleListing.close, region)}
            </span>
          </div>
        </div>
        {raffleListing.close <= MONTH_IN_SEC}
        <ClientOnly>
          {() => (
            <div className="flex items-center justify-center">
              <Countdown
                toUnixTimestamp={raffleListing.close}
                onEnd={() => setHasRaffleEnded(true)}
              />
            </div>
          )}
        </ClientOnly>
      </div>
      <div className="px-4 pb-6">
        {isGated ? (
          <Tracked.Button
            className="flex h-12 w-full items-center justify-center"
            name="GATED_RETAILER_CLICK"
            pagePosition="WTB"
            variant="forest"
            onClick={() => {
              setShowLoginModal(true);
              recordRegistrationStart({ placement: 'raffle_gate' });

              return new RegisterIntentEvent({
                buttonName: 'GATED_RETAILER_CLICK',
                isBot,
                pagePosition: 'WTB'
              }).send();
            }}
          >
            {t('gatedRetailerButton')}
          </Tracked.Button>
        ) : (
          <a
            className="block w-full"
            data-cy="raffle-button"
            onClick={onButtonClick}
            href={url}
            target="_blank"
            rel="sponsored nofollow noopener"
          >
            <Button
              className="flex h-12 w-full items-center justify-center"
              variant="forest"
            >
              {t('enterRaffle')}
            </Button>
          </a>
        )}
      </div>
      {showLoginModal && (
        <Modals.SignUp
          modalType="pdpGate"
          registrationEntry={{ placement: 'raffle_gate' }}
          hideAppDownloadPrompt
          onDismiss={() => setShowLoginModal(false)}
          image={product.gallery[0]}
        />
      )}
    </div>
  );
}
